// client/src/pages/seo/RestaurantWebsiteForRestaurantsEn.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SeoRelatedLinks from "../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "check",
    label: "Does your restaurant need a better website?",
  },
  {
    id: "definition",
    label: "What should a restaurant website do today?",
  },
  {
    id: "visibility",
    label: "Why online visibility matters for restaurants",
  },
  {
    id: "menu",
    label: "The menu is often the most important part of a restaurant website",
  },
  {
    id: "trust",
    label: "A website builds trust before the guest arrives",
  },
  {
    id: "reservation",
    label: "Website, menu and reservation should work together",
  },
  {
    id: "mistakes",
    label: "Common restaurant website mistakes",
  },
  {
    id: "small",
    label: "Do small restaurants need a website too?",
  },
  {
    id: "bestplace",
    label: "How Best Place helps build a restaurant website",
  },
  {
    id: "faq",
    label: "FAQ about restaurant websites",
  },
];

const checklist = [
  "Your restaurant has no website or only a very old one.",
  "Your menu is hard to find online.",
  "Guests often ask about opening hours, prices, or the menu.",
  "You want to connect QR code, menu, and reservation in one place.",
  "You want to depend less on social media platforms.",
  "Your restaurant should be easier to understand in Google and online search.",
  "You want to show photos, offers, or current information more easily.",
  "You want to help guests decide before they visit.",
  "You want a simple page without a complicated agency project.",
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function getResult(score) {
  if (score <= 3) {
    return {
      title: "A simple online presence may be enough for now",
      text:
        "If guests already find your restaurant easily and your most important information is clear online, a new website may not be urgent yet.",
      tone: "low",
    };
  }

  if (score <= 6) {
    return {
      title: "A restaurant website would be useful",
      text:
        "Your restaurant can benefit from a clear website: menu, opening hours, contact, reservation, and QR code can come together in one place.",
      tone: "medium",
    };
  }

  return {
    title: "A website can become your digital center",
    text:
      "Several points suggest that your restaurant should use a website as a central digital place: visibility, menu, reservation, trust, and better orientation for guests.",
    tone: "high",
  };
}

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Visual idea">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idea for illustration:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function RestaurantWebsiteForRestaurantsEn() {
  const [checked, setChecked] = useState([]);

  const score = checked.length;
  const result = useMemo(() => getResult(score), [score]);

  useEffect(() => {
    document.documentElement.setAttribute("lang", "en");
    document.title =
      "Restaurant Website for Restaurants: Menu, Reservation and Trust | Best Place";
  }, []);

  const toggle = (item) => {
    setChecked((prev) =>
      prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]
    );
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="en"
        quickTarget="check"
        quickLabel="Quick check"
        langLinks={{
          en: "/en/restaurant-website-for-restaurants",
          de: "/de/restaurant-website",
          no: "/no/restaurant-nettside",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Restaurant website for restaurants: menu, reservation and trust in
              one place
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                A restaurant website is not just a digital business card. It can
                be the place where guests see your menu, check opening hours,
                view photos, and reserve a table.
              </p>

              <p className="seoHero__text">
                Social media, Google, and review platforms are important. But
                your own website is the space your restaurant controls: content,
                menu, images, offers, reservation, and contact.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="en" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Content of the article</h2>

        <nav className="qrArticle__nav" aria-label="Article navigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="check" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Does your restaurant need a better website?</h2>

            <p>
              Press the statements that sound true for your restaurant. The
              result is intentionally honest: not every restaurant needs a large
              website immediately, but almost every restaurant benefits from one
              clear place for menu, opening hours, contact and reservation.
            </p>

            <div className="qrArticle__checkGrid">
              {checklist.map((item) => (
                <label key={item} className="qrArticle__checkItem">
                  <input
                    type="checkbox"
                    checked={checked.includes(item)}
                    onChange={() => toggle(item)}
                  />
                  <span className="text">{item}</span>
                </label>
              ))}
            </div>

            <div className={`qrArticle__result qrArticle__result--${result.tone}`}>
              <div className="qrArticle__score">
                {score} / {checklist.length}
              </div>

              <div>
                <strong>{result.title}</strong>
                <p>{result.text}</p>
              </div>
            </div>
          </div>
        </section>

        <section id="definition" className="acquaintance">
          <h2>What should a restaurant website do today?</h2>

          <p>
            A good restaurant website answers the guest’s most important
            questions quickly: What can I eat there? Where is the restaurant?
            When is it open? How can I reserve a table?
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Digital menu.</li>
            <li className="text__seo-li">Opening hours.</li>
            <li className="text__seo-li">Address and contact.</li>
            <li className="text__seo-li">Photos of food and atmosphere.</li>
            <li className="text__seo-li">Reservation.</li>
            <li className="text__seo-li">QR code access.</li>
            <li className="text__seo-li">Current offers.</li>
            <li className="text__seo-li">Mobile view.</li>
          </ul>

          <p>
            The best restaurant website is often not the most complicated one.
            It is the one that helps the guest make a decision quickly.
          </p>
        </section>

        <section id="visibility" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Why online visibility matters for restaurants</h2>

            <p>
              Many guests discover restaurants online before they ever stand in
              front of the door: through Google, maps, social media,
              recommendations, or a direct search for menus, opening hours, or
              reservations.
            </p>

            <h3>Your website can become the central destination</h3>

            <p>
              A website is not the only channel, but it can be the central place
              that other channels point to: Google profile, QR code, social
              media, flyers, or digital menu.
            </p>

            <p>
              This helps guests find one clear source instead of jumping between
              different platforms, outdated PDF files, social posts and separate
              contact links.
            </p>
          </div>
        </section>

        <section id="menu" className="acquaintance">
          <h2>
            The menu is often the most important part of a restaurant website
          </h2>

          <p>
            Many visitors come to a restaurant website with a very specific
            question: What can I eat there, and what does it cost?
          </p>

          <p>
            This is why the menu should not be hidden, outdated, or available
            only as a hard-to-read PDF. A mobile digital menu with categories,
            images, and current prices can be much clearer.
          </p>

          <div className="seoCompareSmall">
            <div>
              <strong>Related page</strong>
              <p>
                <Link to="/en/digital-menu-for-restaurants">
                  Digital menu for restaurants
                </Link>
              </p>
            </div>

            <div>
              <strong>Related page</strong>
              <p>
                <Link to="/en/qr-code-menu-for-restaurants">
                  QR code menu for restaurants
                </Link>
              </p>
            </div>
          </div>
        </section>

        <section id="trust" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>A website builds trust before the guest arrives</h2>

            <p>
              Guests do not decide only by price and distance. They also notice
              the impression, how current the information feels, the photos, the
              clarity, and whether the restaurant appears professional.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Current menu.</li>
              <li className="text__seo-li">Real photos.</li>
              <li className="text__seo-li">Clear opening hours.</li>
              <li className="text__seo-li">Contact information.</li>
              <li className="text__seo-li">Reservation option.</li>
              <li className="text__seo-li">Strong mobile experience.</li>
            </ul>

            <p>
              If this information is missing or outdated, the guest may feel
              uncertain. A good website reduces that uncertainty.
            </p>

            <VisualIdea>
              A restaurant website preview on a smartphone: menu, photos,
              opening hours and a clear reservation button in one place.
            </VisualIdea>
          </div>
        </section>

        <section id="reservation" className="acquaintance">
          <h2>Website, menu and reservation should work together</h2>

          <p>
            A restaurant website becomes stronger when it does not only inform,
            but also makes the next step easy. A guest sees the menu, becomes
            interested, and can reserve a table.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">Find restaurant.</li>
            <li className="text__seo-li">View menu.</li>
            <li className="text__seo-li">Check atmosphere.</li>
            <li className="text__seo-li">Reserve table.</li>
          </ol>

          <p>
            This path is easier than jumping between different platforms, PDFs,
            messages, and phone numbers.
          </p>

          <div className="seoCompareSmall">
            <div>
              <strong>Related page</strong>
              <p>
                <Link to="/en/online-table-reservation-for-restaurants">
                  Online table reservation
                </Link>
              </p>
            </div>

            <div>
              <strong>Related page</strong>
              <p>
                <Link to="/en/free-digital-menu-for-restaurants">
                  Free digital menu
                </Link>
              </p>
            </div>
          </div>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Common restaurant website mistakes</h2>

            <p>
              A restaurant website does not need to be large, but it must answer
              the most important guest questions clearly.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Common mistakes</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Old prices or unavailable dishes stay online.
                  </li>
                  <li className="text__seo-li">
                    The menu exists only as a PDF.
                  </li>
                  <li className="text__seo-li">
                    Guests cannot easily find how to reserve.
                  </li>
                  <li className="text__seo-li">
                    The mobile view is weak.
                  </li>
                  <li className="text__seo-li">
                    Address, opening hours or contact details are hard to find.
                  </li>
                  <li className="text__seo-li">
                    Website, QR code and digital menu are not connected.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Better</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Keep the menu current.
                  </li>
                  <li className="text__seo-li">
                    Use a mobile digital menu.
                  </li>
                  <li className="text__seo-li">
                    Add a clear reservation path.
                  </li>
                  <li className="text__seo-li">
                    Make the website comfortable on smartphones.
                  </li>
                  <li className="text__seo-li">
                    Show opening hours, address and contact clearly.
                  </li>
                  <li className="text__seo-li">
                    Connect website, QR code and menu in one flow.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="small" className="acquaintance">
          <h2>Do small restaurants need a website too?</h2>

          <p>
            Small restaurants often think that social media or Google is enough.
            These channels are important, but they do not fully belong to the
            restaurant. A website gives more control.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>External platforms only</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Less control.</li>
                <li className="text__seo-li">
                  Dependent on other platforms’ layout and rules.
                </li>
                <li className="text__seo-li">
                  Information often spread across channels.
                </li>
                <li className="text__seo-li">
                  Menu and reservation not always connected.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Own restaurant website</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Central place for guests.</li>
                <li className="text__seo-li">Own digital menu.</li>
                <li className="text__seo-li">Clear reservation.</li>
                <li className="text__seo-li">QR code destination.</li>
                <li className="text__seo-li">
                  Better controlled presentation.
                </li>
              </ul>
            </div>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="en" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>Frequently asked questions about restaurant websites</h2>

          <div className="seoFaq">
            <details>
              <summary>Does every restaurant need its own website?</summary>
              <p>
                Not every restaurant needs a large website immediately. But
                almost every restaurant benefits from one clear place for menu,
                opening hours, contact, and reservation.
              </p>
            </details>

            <details>
              <summary>Is social media enough?</summary>
              <p>
                Social media is useful, but not fully controllable. A website is
                more stable as a central place for important information.
              </p>
            </details>

            <details>
              <summary>What is the most important content?</summary>
              <p>
                For many guests, the menu, opening hours, address, contact,
                photos, and reservation are the most important parts.
              </p>
            </details>

            <details>
              <summary>Should the menu be embedded as a PDF?</summary>
              <p>
                A PDF is possible, but often uncomfortable on smartphones. A
                mobile digital menu is usually more user-friendly.
              </p>
            </details>

            <details>
              <summary>Can a website be connected with a QR code?</summary>
              <p>
                Yes. A QR code can lead guests directly to the menu or the
                restaurant page.
              </p>
            </details>

            <details>
              <summary>Can I start small?</summary>
              <p>
                Yes. A good start can include menu, QR code, contact, and a
                simple restaurant page.
              </p>
            </details>
          </div>
        </section>
        <SeoRelatedLinks
          lang="en"
          group="business"
          exclude="/en/restaurant-website-for-restaurants"
        />
      </article>
    </main>
  );
}